'use client';

import type { Placement } from '@popperjs/core';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { Portal } from 'react-overlays';
import { usePopper } from 'react-popper';

import { type DropdownContextType, useDropdownContext } from './context';
import { List } from './List/List';

import styles from './Menu.module.scss';

type RenderFunction = (context: DropdownContextType) => ReactNode;

export interface Props {
    children: ReactNode | RenderFunction;
    className?: string;
    popperClassName?: string;
    placement?: Placement;
}

// TODO: Implement keyboard handling (arrow keys to navigate, tab to close)
export function Menu({ children, className, popperClassName, placement = 'bottom-start' }: Props) {
    const dropdownContext = useDropdownContext();
    const {
        dropdownId,
        isMounted,
        popperReference,
        setPopperReference,
        stage,
        triggerId,
        triggerReference,
    } = dropdownContext;

    const { styles: popperStyles, attributes } = usePopper(triggerReference, popperReference, {
        modifiers: [{ name: 'preventOverflow' }, { name: 'offset', options: { offset: [0, 2] } }],
        placement,
    });

    if (!isMounted) {
        return null;
    }

    return (
        <Portal container={document.body}>
            <div
                {...attributes.popper}
                className={classNames(styles.popper, popperClassName)}
                ref={setPopperReference}
                style={popperStyles.popper}
            >
                <List
                    className={classNames(styles.menu, className, {
                        [styles.opening]: stage === 'opening',
                        [styles.open]: stage === 'open',
                        [styles.closing]: stage === 'closing',
                    })}
                    id={dropdownId}
                    triggerId={triggerId}
                >
                    {typeof children === 'function' ? children(dropdownContext) : children}
                </List>
            </div>
        </Portal>
    );
}
